import Toast from 'react-native-root-toast';

import i18n, { t } from '../../i18n';

/**
 * Standard error messages for common API error scenarios.
 *
 * Resolved through `t` on every access rather than held as strings, so a
 * language change is picked up without reloading the module.
 */
const ERROR_MESSAGES = {
  get NETWORK_ERROR() {
    return t('errors:network');
  },
  get UNAUTHORIZED() {
    return t('errors:unauthorized');
  },
  get FORBIDDEN() {
    return t('errors:forbidden');
  },
  get NOT_FOUND() {
    return t('errors:notFound');
  },
  get SERVER_ERROR() {
    return t('errors:server');
  },
  get TIMEOUT() {
    return t('errors:timeout');
  },
  get UNKNOWN() {
    return t('errors:unknown');
  },
};

/**
 * Extract a user-friendly message from an API error
 *
 * @param {Error|Object} error - The error object
 * @returns {string} A user-friendly error message
 */
export function getErrorMessage(error) {
  if (!error) {
    return ERROR_MESSAGES.UNKNOWN;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Check for network errors
  if (error.message?.includes('Network') || error.message?.includes('fetch')) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  // Prefer a stable error code over the server's English prose. Backends attach
  // `code` alongside `error`; an unmapped or absent code falls through to the
  // raw message below, so endpoints can adopt codes one at a time without the
  // client needing to know which have.
  const responseCode =
    error.code ||
    error.response?.data?.code ||
    error.response?.body?.code ||
    error.response?.code ||
    error.body?.code;
  if (responseCode && typeof responseCode === 'string') {
    const key = `errors:codes.${responseCode}`;
    if (i18n.exists(key)) {
      return t(key);
    }
  }

  // Check for custom error message in response body first (from API)
  // This captures descriptive errors like "Email domain not allowed for this organization"
  // Amplify v6 may store the error in different places
  const responseError =
    error.error || // Direct error property (response body merged into error)
    error.response?.data?.error || // Axios-style response
    error.response?.body?.error || // Amplify v6 response body
    error.response?.error || // Direct response error
    error.body?.error; // Alternative Amplify structure
  if (responseError && typeof responseError === 'string') {
    return responseError;
  }

  // Check if error.message contains JSON with an error field (common in Amplify API errors)
  if (error.message && !error.message.includes('Network') && !error.message.includes('fetch')) {
    try {
      // Some API errors include JSON in the message
      const jsonMatch = error.message.match(/\{.*\}/s);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.error && typeof parsed.error === 'string') {
          return parsed.error;
        }
      }
    } catch {
      // Not JSON, continue with other checks
    }
  }

  // Check for HTTP status codes (only for generic errors without custom messages)
  const status = error.status || error.statusCode || error.response?.status;
  if (status) {
    switch (status) {
      case 401:
        return ERROR_MESSAGES.UNAUTHORIZED;
      case 403:
        return ERROR_MESSAGES.FORBIDDEN;
      case 404:
        return ERROR_MESSAGES.NOT_FOUND;
      case 408:
        return ERROR_MESSAGES.TIMEOUT;
      case 500:
      case 502:
      case 503:
      case 504:
        return ERROR_MESSAGES.SERVER_ERROR;
    }
  }

  // Check for Amplify API errors
  if (error.message?.includes('API name is invalid')) {
    return ERROR_MESSAGES.SERVER_ERROR;
  }

  // Use error message if available
  if (error.message) {
    return error.message;
  }

  return ERROR_MESSAGES.UNKNOWN;
}

/**
 * Centralized API error handler
 *
 * Provides consistent error handling across the app:
 * - Logs errors to console
 * - Shows user-friendly toast notifications
 * - Supports custom callbacks for additional handling
 *
 * @param {Error|Object} error - The error to handle
 * @param {Object} options - Configuration options
 * @param {boolean} options.silent - If true, don't show toast (default: false)
 * @param {string} options.userMessage - Custom message to show user (overrides auto-detection)
 * @param {string} options.context - Context string for logging (e.g., 'fetching reports')
 * @param {Function} options.onError - Callback function called with the error
 * @param {boolean} options.showToast - Whether to show toast (default: true unless silent)
 *
 * @example
 * // Basic usage
 * Util.get('Resty', '/items').catch(err => handleApiError(err));
 *
 * // With custom message
 * Util.get('Resty', '/items').catch(err =>
 *   handleApiError(err, { userMessage: 'Failed to load reports' })
 * );
 *
 * // Silent (logging only)
 * Util.get('Resty', '/items').catch(err =>
 *   handleApiError(err, { silent: true })
 * );
 *
 * // With callback
 * Util.get('Resty', '/items').catch(err =>
 *   handleApiError(err, { onError: (e) => setError(e) })
 * );
 */
export function handleApiError(error, options = {}) {
  const {
    silent = false,
    userMessage = null,
    context = null,
    onError = null,
    showToast = !silent,
  } = options;

  // Build log message
  const contextStr = context ? ` [${context}]` : '';
  console.error(`[API Error]${contextStr}`, error);

  // Show toast notification if not silent
  if (showToast) {
    const message = userMessage || getErrorMessage(error);
    Toast.show(message, {
      backgroundColor: '#d32f2f',
      textColor: '#fff',
      duration: Toast.durations.LONG,
      position: Toast.positions.BOTTOM,
      shadow: true,
      animation: true,
    });
  }

  // Call custom error handler if provided
  if (onError && typeof onError === 'function') {
    onError(error);
  }

  return error;
}

/**
 * Wrapper to create an error handler with preset options
 *
 * @param {Object} defaultOptions - Default options for all calls
 * @returns {Function} Error handler function
 *
 * @example
 * const handleReportError = createErrorHandler({ context: 'Reports' });
 * Util.get('Resty', '/items').catch(handleReportError);
 */
export function createErrorHandler(defaultOptions = {}) {
  return (error, overrideOptions = {}) => {
    return handleApiError(error, { ...defaultOptions, ...overrideOptions });
  };
}

export default handleApiError;
